import type { SectorPerformance } from "../../services/pulseService";
import { formatPercent, changeColor } from "./utils";

interface Props {
  sectors: SectorPerformance[];
}

export function SectorHeatmap({ sectors }: Props) {
  if (!sectors.length) {
    return (
      <div className="glass-card p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider mb-3">Sector Performance</h3>
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-surface-500 text-xs">
            <div className="h-2 w-2 rounded-full bg-surface-600 animate-pulse" />
            Loading sector data...
          </div>
        </div>
      </div>
    );
  }

  const maxAbs = Math.max(...sectors.map((s) => Math.abs(s.change_percent)), 1);

  return (
    <div className="glass-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider mb-3">
        Sector Performance
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {sectors
          .sort((a, b) => b.change_percent - a.change_percent)
          .map((s) => {
            const intensity = Math.min(Math.abs(s.change_percent) / maxAbs, 1);
            const bg =
              s.change_percent >= 0
                ? `rgba(20, 184, 166, ${intensity * 0.25})`
                : `rgba(239, 68, 68, ${intensity * 0.25})`;
            const border =
              s.change_percent >= 0
                ? `rgba(20, 184, 166, ${intensity * 0.4})`
                : `rgba(239, 68, 68, ${intensity * 0.4})`;

            return (
              <div
                key={s.symbol}
                className="rounded-xl p-3 flex flex-col items-center justify-center text-center transition-transform hover:scale-[1.02]"
                style={{ background: bg, border: `1px solid ${border}` }}
              >
                <span className="text-xs text-surface-300 font-medium truncate w-full">
                  {s.name}
                </span>
                <span className={`text-lg font-bold tabular-nums mt-1 ${changeColor(s.change_percent)}`}>
                  {formatPercent(s.change_percent)}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
}
